import styled from "@emotion/styled"
import { css } from "@emotion/react"
import Typography from "@mui/material/Typography"
import { colors } from "../../theme/colors"
import { breakpoints } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"

/* ------------------------------------------------------------------ */
/* Root — white rounded card with vertical padding                     */
/* ------------------------------------------------------------------ */
export const Root = styled.div`
  width: 100%;
  background-color: ${colors.primitives.neutral0};
  border-radius: ${sizes.borderRadius[24]}px;
  padding: ${sizes.spacing[48]}px ${sizes.spacing[16]}px;
  overflow: hidden;

  @media (min-width: ${breakpoints.lg}px) {
    max-width: calc(100% - 160px);
    margin: 0 auto;
    border-radius: ${sizes.borderRadius[40]}px;
    padding: ${sizes.spacing[64]}px 80px;
  }

  @media (min-width: ${breakpoints.xl}px) {
    max-width: ${breakpoints.xl}px;
    padding: ${sizes.spacing[64]}px ${sizes.spacing[120]}px;
  }

  @media (min-width: ${breakpoints.xxl}px) {
    max-width: ${breakpoints.xxl}px;
    padding: ${sizes.spacing[64]}px 360px;
  }
`

/* ------------------------------------------------------------------ */
/* Layout — column on mobile, row on desktop; entrance animation       */
/* ------------------------------------------------------------------ */
export const Layout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${sizes.spacing[40]}px;

  @media (min-width: ${breakpoints.lg}px) {
    flex-direction: row;
    align-items: center;
    gap: ${sizes.spacing[64]}px;
  }
`

/* ------------------------------------------------------------------ */
/* Numbers animation section — contains title + stats grid              */
/* ------------------------------------------------------------------ */
export const NumbersSection = styled("div", {
  shouldForwardProp: (prop) => prop !== "$visible",
})<{ $visible: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[40]}px;
  width: 100%;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: translateY(${(p) => (p.$visible ? "0" : "24px")});
  /* EGDE-337 beat 2 ("Design"): reveal the title + numbers a moment after the
     media enters (beat 1), so the media-first "Transition" stage reads first. */
  transition:
    opacity 0.6s ease-out 0.35s,
    transform 0.6s ease-out 0.35s;

  @media (min-width: ${breakpoints.lg}px) {
    flex: 1 0 0;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

/* ------------------------------------------------------------------ */
/* Title row — chevron icon + title text                                */
/* ------------------------------------------------------------------ */
export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: flex-start;
  width: 100%;
`

/* ------------------------------------------------------------------ */
/* Title text                                                          */
/* ------------------------------------------------------------------ */
export const Title = styled(Typography)`
  color: ${colors.light.text.primary};
`

/* ------------------------------------------------------------------ */
/* Chevron wrapper                                                     */
/* ------------------------------------------------------------------ */
export const ChevronWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;

  [dir="ltr"] & svg {
    /* @noflip */
    transform: scaleX(-1);
  }
`

/* ------------------------------------------------------------------ */
/* Statistics grid — column on mobile, 2x2 on desktop                  */
/* ------------------------------------------------------------------ */
export const StatsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[40]}px;
  width: 100%;

  @media (min-width: ${breakpoints.lg}px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
`

/* ------------------------------------------------------------------ */
/* Stats row — groups 2 items on desktop                               */
/* ------------------------------------------------------------------ */
export const StatsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[40]}px;

  @media (min-width: ${breakpoints.lg}px) {
    display: contents;
  }
`

/* ------------------------------------------------------------------ */
/* Individual stat item — strip + content                              */
/* ------------------------------------------------------------------ */
export const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[16]}px;
  justify-content: flex-start;
  width: 100%;
  padding: 0;

  @media (min-width: ${breakpoints.lg}px) {
    align-items: stretch;
    width: auto;
    padding: ${sizes.spacing[8]}px 0;
  }
`

/* ------------------------------------------------------------------ */
/* Stat content — number + description                                 */
/* ------------------------------------------------------------------ */
export const StatContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[8]}px;
  flex: 1 0 0;
  align-items: flex-start;
  justify-content: center;

  @media (min-width: ${breakpoints.lg}px) {
    justify-content: flex-start;
  }
`

/* ------------------------------------------------------------------ */
/* Number text (uses "number" variant from theme)                      */
/* ------------------------------------------------------------------ */
export const NumberText = styled(Typography)`
  color: ${colors.light.text.primary};
  font-weight: 700 !important;
`

/* ------------------------------------------------------------------ */
/* Description text                                                    */
/* ------------------------------------------------------------------ */
export const DescriptionText = styled(Typography)`
  color: ${colors.light.text.primary};
`

/* ------------------------------------------------------------------ */
/* Number strip — vertical pill bar track                              */
/* ------------------------------------------------------------------ */
export const StripTrack = styled.div`
  width: 12px;
  height: 116px;
  border-radius: ${sizes.borderRadius.full}px;
  background-color: ${colors.primitives.neutral200};
  position: relative;
  overflow: hidden;
  flex-shrink: 0;

  @media (min-width: ${breakpoints.lg}px) {
    height: auto;
    align-self: stretch;
  }
`

/* ------------------------------------------------------------------ */
/* Number strip — orange fill                                          */
/* ------------------------------------------------------------------ */
export const StripFill = styled.div<{
  $active: boolean
  $delay: number
  $animate: boolean
  $index: number
  $count: number
}>`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background-color: ${colors.primitives.secondary500};
  /* EGDE-510 point B2 (Raz, 2026-07-29): "The orange strip fills from bottom to
     top. Desired state is to fill from top to bottom inside the strip." The
     origin was "bottom", which grows the fill upwards. */
  transform-origin: top;

  /* EGDE-318: animation toggle off → strips render full, nothing animates. */
  ${(p) =>
    !p.$animate &&
    css`
      transform: scaleY(1);
    `}

  ${(p) =>
    p.$animate &&
    css`
      /* Mobile: the fill is SCRUBBED by the scroll — --strip-progress is the
         BLOCK's 0..1 progress, written once per frame on the stats grid.
         No transition: the scroll position IS the animation timeline.

         EGDE-510 point B2: "Scrolling with the thumb: the first strip fills,
         then the second, then the third, not all at once." Each strip used to
         scrub against its OWN viewport position, and because the stats sit only
         ~130px apart their windows overlapped almost completely — measured at
         375px the first three strips read 0.37 / 0.10 / 0.00 and then
         0.84 / 0.76 / … at the same scroll offsets, i.e. filling together.
         Slicing the block progress into N consecutive windows makes strip i own
         exactly [i/N, (i+1)/N], so each one starts only once the previous has
         topped out. */
      --strip-p: clamp(
        0,
        calc(var(--strip-progress, 0) * ${p.$count} - ${p.$index}),
        1
      );
      transform: scaleY(var(--strip-p));

      @media (min-width: ${breakpoints.lg}px) {
        /* Desktop keeps the EGDE-337 staggered auto-fill — "the strip fills
           automatically, one after the other" per the Figma desktop note. */
        transform: scaleY(${p.$active ? 1 : 0});
        transition: transform 1.2s cubic-bezier(0.33, 1, 0.68, 1);
        transition-delay: ${p.$delay}ms;
      }

      @media (prefers-reduced-motion: reduce) {
        transform: scaleY(1);
        transition: none;
      }
    `}
`

/* ------------------------------------------------------------------ */
/* Media wrapper (image or video)                                      */
/*                                                                     */
/* EGDE-337 beat 1 ("Transition"): the media enters first, before the   */
/* numbers reveal. On mobile it grows from a small thumbnail anchored to */
/* the bottom of its box (Figma: ~143px inside the 343px box, so         */
/* scale 143/343 ≈ 0.417, transform-origin bottom) up to full size. On   */
/* desktop / 1440 the side image is already full-size, so it only fades  */
/* in (no grow). Driven by `$entered` (viewport entry).                  */
/* ------------------------------------------------------------------ */
export const MediaWrapper = styled("div", {
  shouldForwardProp: (prop) => prop !== "$entered",
})<{ $entered?: boolean }>`
  position: relative;
  width: 343px;
  height: 343px;
  flex-shrink: 0;

  opacity: ${(p) => (p.$entered ? 1 : 0)};
  transform: scale(${(p) => (p.$entered ? 1 : 143 / 343)});
  transform-origin: center bottom;
  transition:
    opacity 0.6s ease-out,
    transform 0.6s cubic-bezier(0.33, 1, 0.68, 1);

  @media (min-width: ${breakpoints.lg}px) {
    width: 380px;
    height: 380px;
    flex-shrink: 0;
    /* Desktop incl. 1440: image is full-size from the start of the
       transition — fade only, no grow. */
    transform: none;
  }

  @media (min-width: ${breakpoints.xl}px) {
    width: 540px;
    height: 540px;
  }

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }
`

/* EGDE-529: crop window for the desktop "full image transition". Anchored at
   the media's outer (inline-end) edge, it starts spanning the whole Layout
   row — covering the numbers area, exactly like the prototype — and narrows
   to the resting card as the scroll scrub brings --img-extra to 0. The var
   defaults to 0px, so no-JS / reduced-motion / toggle-off render the resting
   card. Mobile ignores the var and keeps the thumbnail-grow entrance. */
export const MediaCrop = styled.div`
  position: absolute;
  inset-block: 0;
  inset-inline-end: 0;
  width: 100%;
  border-radius: ${sizes.borderRadius[24]}px;
  overflow: hidden;

  @media (min-width: ${breakpoints.lg}px) {
    border-radius: ${sizes.borderRadius[40]}px;
    width: calc(100% + var(--img-extra, 0px));
    /* EGDE-529 (r4): 2.6s easeOutQuart is the curve measured off the reference
       prototype — see the constants block in index.tsx. r3 ran this in 0.9s on
       a harder ease (quint), which put half the travel into the first ~150ms
       and read as a snap rather than a transition.
       --img-crop-transition is set to none for the frame that paints the wide
       state, so the entrance starts from spanning rather than animating in
       from the resting card. */
    transition: var(
      --img-crop-transition,
      width 2.6s cubic-bezier(0.25, 1, 0.5, 1)
    );
  }
`

/* ------------------------------------------------------------------ */
/* Video element — covers the media wrapper                            */
/* ------------------------------------------------------------------ */
export const MediaVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`
